import { z } from 'zod';

// Local e hotéis do evento: opcionais, fora de `MICE_EVENT_RULES`, `rulesPending` e
// `missingPremises`. Termos do Google: só o place id é persistido; o resto é lido na hora.
// Este arquivo não importa de ./mice (que o importa): o IATA é o mesmo `IataSchema` de lá.

const PlaceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{10,}$/);
const PlaceNameSchema = z.string().min(1).max(160);
const IataCodeSchema = z.string().regex(/^[A-Z]{3}$/);

export const MiceEventVenueSchema = z.object({ placeId: PlaceIdSchema, name: PlaceNameSchema });
export type MiceEventVenue = z.infer<typeof MiceEventVenueSchema>;

/** `placeId` nulo: o hotel veio dito à mão, sem lugar do Google para ler. */
export const MiceEventHotelSchema = z
  .object({
    placeId: PlaceIdSchema.nullable(),
    name: PlaceNameSchema,
    checkIn: z.iso.date(),
    checkOut: z.iso.date(),
  })
  .refine((hotel) => hotel.checkOut >= hotel.checkIn, {
    message: 'checkOut antes do checkIn',
    path: ['checkOut'],
  });
export type MiceEventHotel = z.infer<typeof MiceEventHotelSchema>;

export const MICE_MAX_HOTELS = 10;

export const MiceEventPlacesSchema = z.object({
  venue: MiceEventVenueSchema.nullable(),
  hotels: z.array(MiceEventHotelSchema).max(MICE_MAX_HOTELS),
});
export type MiceEventPlaces = z.infer<typeof MiceEventPlacesSchema>;

export const EMPTY_MICE_PLACES: MiceEventPlaces = { venue: null, hotels: [] };

/** Os mesmos lugares, campo a campo (a ordem das chaves não conta; a dos hotéis, sim): a `base`
 *  de um `PUT places` confere com os gravados. */
export function sameMiceEventPlaces(a: MiceEventPlaces, b: MiceEventPlaces): boolean {
  const key = ({ venue, hotels }: MiceEventPlaces) =>
    JSON.stringify([
      venue && [venue.placeId, venue.name],
      hotels.map((h) => [h.placeId, h.name, h.checkIn, h.checkOut]),
    ]);
  return key(a) === key(b);
}

/** O nome do hotel para comparar: sem acento, minúsculas e sem o "hotel " do começo
 *  ("Hotel Atlântico" casa com "atlantico"). */
export function normalizeHotelName(name: string): string {
  return name
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/^hotel\s+/, '');
}

/** Na URL cada lista vem em CSV (`from=GRU,CGH`); aceita array já separado. */
const csv = (value: unknown): unknown => (typeof value === 'string' ? value.split(',') : value);

export const MicePlaceLegsQuerySchema = z.object({
  from: z.preprocess(csv, z.array(IataCodeSchema).min(1).max(4)),
  to: z.preprocess(csv, z.array(PlaceIdSchema).min(1).max(11)),
});
export type MicePlaceLegsQuery = z.infer<typeof MicePlaceLegsQuerySchema>;

export const MicePlaceLegSchema = z.object({
  iata: IataCodeSchema,
  placeId: PlaceIdSchema,
  meters: z.number(),
  durationSec: z.number(),
});
export type MicePlaceLeg = z.infer<typeof MicePlaceLegSchema>;

/** O par que falhou não aparece. */
export const MicePlaceLegsSchema = z.object({ legs: z.array(MicePlaceLegSchema) });
export type MicePlaceLegs = z.infer<typeof MicePlaceLegsSchema>;
